import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { JobApplicationService } from '../../../../services/job-application';
import { ResigteringService } from '../../../../services/resigtering.service';

const APPLICATION_STATUSES = [
  'pending_review',
  'contacted',
  'interview_scheduled',
  'interview_done',
  'documents_completion',
  'client_review',
  'client_approved',
  'accepted',
  'in_process',
  'medical_check',
  'travel_preparation',
  'hired',
  'traveled',
  'rejected',
  'rejected_by_client',
  'postponed',
  'on_hold',
  'no_response',
  'not_qualified',
  'waiting_for_data',
  'cancelled',
  'withdrawn',
] as const;

type KnownApplicationStatus = (typeof APPLICATION_STATUSES)[number];
type ApplicationStatus = KnownApplicationStatus | string;

const LEGACY_STATUS_MAP: Record<string, KnownApplicationStatus> = {
  pending: 'pending_review',
  reviewed: 'contacted',
  shortlisted: 'client_review',
};

@Component({
  selector: 'app-myapplies',
  templateUrl: './myapplies.component.html',
  styleUrl: './myapplies.component.scss',
})
export class MyappliesComponent implements OnInit {
  applications: any[] = [];
  currentPage = 1;
  lastPage = 1;
  total = 0;

  isLoading = true;
  errorMessage = '';

  constructor(
    private applicationService: JobApplicationService,
    private userService: ResigteringService,
    private transloco: TranslocoService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const userId = this.getUserIdFromStorage();

    if (userId) {
      this.loadApplications(userId);
      return;
    }

    this.userService.getUserId().subscribe({
      next: (res) => {
        if (!res?.user_id) {
          this.handleError('atw.myApplies.errors.userIdNotFound');
          return;
        }

        this.loadApplications(res.user_id);
      },
      error: () => this.handleError('atw.myApplies.errors.userIdNotFound'),
    });
  }

  loadApplications(userId: number | string, page = 1): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.applicationService.getApplicationsByUserId(userId, page).subscribe({
      next: (res) => {
        const payload = res?.data ?? res?.applications ?? res;

        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];

        this.applications = list;
        this.currentPage = payload?.current_page || res?.current_page || page;
        this.lastPage = payload?.last_page || res?.last_page || 1;
        this.total = payload?.total || res?.total || list.length;
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage =
          err?.error?.message || this.t('atw.myApplies.errors.loadFailed');
      },
    });
  }

  retryLoad(): void {
    const userId = this.getUserIdFromStorage();

    if (userId) {
      this.loadApplications(userId, this.currentPage);
      return;
    }

    this.ngOnInit();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.lastPage || page === this.currentPage) {
      return;
    }

    const userId = this.getUserIdFromStorage();

    if (userId) {
      this.loadApplications(userId, page);
      return;
    }

    this.ngOnInit();
  }

  openJob(application: any): void {
    const jobId = this.getJob(application)?.id || application?.job_id;

    if (jobId) {
      this.router.navigate(['/job', jobId]);
    }
  }

  trackByApplicationId(index: number, application: any): number | string {
    return application?.id || application?.job_id || index;
  }

  getJob(application: any): any {
    return (
      application?.job ||
      application?.ahmed_elshahat_job ||
      application?.job_data ||
      null
    );
  }

  getJobTitle(application: any): string {
    const job = this.getJob(application) || application;

    return (
      this.pickLang(job, 'title_ar', 'title_en', 'title') ||
      application?.job_title ||
      this.t('atw.myApplies.untitledJob')
    );
  }

  getCompanyName(application: any): string {
    const job = this.getJob(application);
    const company = job?.company || job?.company_data || application?.company;

    return (
      this.pickLang(company, 'name_ar', 'name_en', 'name') ||
      this.pickLang(
        company,
        'company_name_ar',
        'company_name_en',
        'company_name'
      ) ||
      this.pickLang(job, 'company_name_ar', 'company_name_en', 'company_name') ||
      application?.company_name ||
      this.t('atw.myApplies.confidentialEmployer')
    );
  }

  getLocation(application: any): string {
    const job = this.getJob(application);

    const city =
      this.pickLang(job?.city, 'city_name_ar', 'city_name_en', 'name') ||
      this.pickLang(job, 'city_name_ar', 'city_name_en', 'city_name');

    const country =
      this.pickLang(
        job?.country || job?.city?.country,
        'country_name_ar',
        'country_name_en',
        'name'
      ) ||
      this.pickLang(
        job,
        'country_name_ar',
        'country_name_en',
        'country_name'
      );

    return (
      [city, country].filter(Boolean).join(', ') ||
      this.t('atw.myApplies.notSpecified')
    );
  }

  getApplicationDate(application: any): string {
    return this.formatDate(
      application?.submitted_at ||
        application?.applied_at ||
        application?.created_at ||
        application?.updated_at
    );
  }

  getStatus(application: any): ApplicationStatus {
    return this.normalizeStatus(application?.status);
  }

  getStatusLabel(status: ApplicationStatus): string {
    const normalizedStatus = this.normalizeStatus(status);
    const key = `atw.myApplies.status.${normalizedStatus}`;
    const translated = this.t(key);

    if (translated && translated !== key) {
      return translated;
    }

    return this.humanizeStatus(normalizedStatus);
  }

  getStatusClass(status: ApplicationStatus): string {
    const normalizedStatus = this.normalizeStatus(status);

    return `status-${String(normalizedStatus)
      .toLowerCase()
      .replace(/_/g, '-')}`;
  }

  formatDate(dateValue?: string | null): string {
    if (!dateValue) {
      return this.t('atw.myApplies.notSpecified');
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return this.t('atw.myApplies.notSpecified');
    }

    return date.toLocaleString(this.getDateLocale(), {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }

  private normalizeStatus(status?: any): ApplicationStatus {
    const value = String(status || 'pending_review')
      .trim()
      .toLowerCase();

    return LEGACY_STATUS_MAP[value] || value;
  }

  private humanizeStatus(status: ApplicationStatus): string {
    return String(status || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private pickLang(
    source: any,
    arKey: string,
    enKey: string,
    fallbackKey?: string
  ): string {
    if (!source) {
      return '';
    }

    const isArabic = this.transloco.getActiveLang() === 'ar';
    const primary = isArabic ? source?.[arKey] : source?.[enKey];
    const secondary = isArabic ? source?.[enKey] : source?.[arKey];

    return primary || secondary || (fallbackKey ? source?.[fallbackKey] : '') || '';
  }

  private getDateLocale(): string {
    return this.transloco.getActiveLang() === 'ar' ? 'ar-EG' : 'en-US';
  }

  private getUserIdFromStorage(): number | null {
    const storedUser = localStorage.getItem('user');

    if (!storedUser) {
      return null;
    }

    try {
      const user = JSON.parse(storedUser);
      return user?.id || null;
    } catch {
      return null;
    }
  }

  private handleError(messageKey: string): void {
    this.errorMessage = this.t(messageKey);
    this.isLoading = false;
  }

  private t(key: string): string {
    return this.transloco.translate(key);
  }
}